import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Store, Star, Search, ArrowLeft, ShoppingBag, MapPin,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';
import Navigation from '../components/Navigation';
import { MyFoodDocsBadge } from '../components/MyFoodDocuments';
import MyShopMap from '../components/MyShopMap';

// "mymap" is a virtual tab rendered as an interactive Leaflet map instead
// of the shop grid. "all" stays the default selection.
const CATS = ['mymap', 'all', 'fashion', 'beauty', 'myfood', 'art', 'music', 'handcraft',
              'electronics', 'home', 'wellness', 'services', 'education',
              'kids', 'sport', 'jewelry', 'books', 'other'];

const ShopMarketplace = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  // Per-mount random seed: rebuilt every time the user enters or refocuses
  // the marketplace, so shops are shuffled differently on each visit.
  const [seed, setSeed] = useState(() => Date.now().toString(36));
  const qTimer = useRef(null);

  // Regenerate seed whenever the tab/app becomes visible again
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setSeed(Date.now().toString(36));
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    // Skip the grid feed fetch when the "MyMap" pseudo-tab is active — the
    // MyShopMap component pulls its own data from /api/shop/map.
    if (category === 'mymap') return;
    setPage(0); load(0, true); /* eslint-disable-line */
  }, [language, category, seed]);

  const load = async (p = page, replace = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ language, page: String(p), per_page: '30', seed });
      if (category && category !== 'all') params.set('category', category);
      if (query.trim()) params.set('q', query.trim());
      const r = await axios.get(`/api/shop/feed?${params.toString()}`);
      const items = r.data?.shops || [];
      setShops(prev => replace ? items : [...prev, ...items]);
      setHasMore(!!r.data?.has_more);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (val) => {
    setQuery(val);
    if (qTimer.current) clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => { setPage(0); load(0, true); }, 350);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-950 via-black to-violet-950 text-white pb-24" data-testid="shop-marketplace">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-black/80 backdrop-blur-md border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/films?tab=studio')} className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="shop-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold flex items-center gap-2 flex-1">
            <Store className="w-5 h-5 text-fuchsia-400" /> {t('eshop.title', 'MyShop')}
          </h1>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={query}
            onChange={e => handleSearch(e.target.value)}
            placeholder={t('eshop.search_placeholder', 'Rechercher une boutique ou un produit…')}
            className="w-full bg-white/5 border border-white/10 rounded-full pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-fuchsia-500"
            data-testid="shop-search-input"
          />
        </div>
        <div className="flex overflow-x-auto gap-1.5 mt-3 -mx-4 px-4 scrollbar-hide" data-testid="shop-categories">
          {CATS.map(c => (
            <button key={c} onClick={() => setCategory(c)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                category === c ? 'bg-fuchsia-600 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'
              }`}
              data-testid={`shop-cat-${c}`}>
              {c === 'mymap' && <MapPin className="w-3 h-3" />}
              {c === 'mymap' ? (t('eshop.cat_mymap') || 'MyMap') : (t(`eshop.cat_${c}`) || c)}
            </button>
          ))}
        </div>
      </header>

      {/* Grid — or the MyMap interactive map when the "mymap" pseudo-tab is picked. */}
      <main className="px-4 py-4">
        {category === 'mymap' ? (
          <MyShopMap />
        ) : loading && shops.length === 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="aspect-[3/4] rounded-2xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : shops.length === 0 ? (
          <div className="text-center py-20 rounded-2xl bg-white/5 border border-white/10" data-testid="shop-empty">
            <ShoppingBag className="w-12 h-12 mx-auto mb-2 text-gray-500" />
            <p className="text-sm text-gray-400">{t('eshop.empty', 'Aucune boutique pour le moment')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {shops.map(shop => (
              <button key={shop.id} onClick={() => navigate(`/shop/${shop.id}`)}
                className="bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-2xl overflow-hidden text-left hover:border-fuchsia-500/40"
                data-testid={`shop-card-${shop.id}`}>
                <div className="relative aspect-[4/3] bg-gradient-to-br from-fuchsia-700/30 to-violet-800/30">
                  {shop.cover_url ? (
                    <img src={getMediaUrl(shop.cover_url)} alt={shop.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Store className="w-8 h-8 text-white/30" />
                    </div>
                  )}
                  {shop.country && (
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/50 backdrop-blur text-[10px] font-semibold">{shop.country}</span>
                  )}
                </div>
                <div className="p-2.5 space-y-1">
                  <h4 className="font-semibold text-sm truncate">{shop.name}</h4>
                  <p className="text-[11px] text-gray-400 line-clamp-2">{shop.description}</p>
                  <div className="pt-0.5"><MyFoodDocsBadge shop={shop} /></div>
                  <div className="flex items-center gap-2 text-[10px] text-gray-400 pt-1">
                    {shop.rating_count > 0 ? (
                      <span className="flex items-center gap-0.5">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        {shop.rating_avg?.toFixed?.(1) || shop.rating_avg}
                        <span className="text-gray-500">· {shop.rating_count} {t('eshop.reviews_count', 'avis')}</span>
                      </span>
                    ) : (
                      <span className="text-gray-500">{t('eshop.no_reviews_short', 'Pas d\'avis')}</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
        {hasMore && shops.length > 0 && (
          <button
            onClick={() => { const np = page + 1; setPage(np); load(np); }}
            disabled={loading}
            className="mt-4 w-full py-3 rounded-full bg-white/5 hover:bg-white/10 text-sm font-semibold disabled:opacity-50"
            data-testid="shop-load-more"
          >
            {loading ? (t('common.loading', 'Chargement…')) : (t('eshop.load_more', 'Voir plus'))}
          </button>
        )}
      </main>

      <Navigation />
    </div>
  );
};

export default ShopMarketplace;
